import { useEffect, useState, type ReactNode } from 'react'
import {
  Ban,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  Loader2,
  Pencil,
  Plus,
  Save,
  ShieldOff,
  Trash2,
  UserPlus,
  Users
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  assignPlan,
  dealerSelected,
  deleteDealer,
  deleteSubDealer,
  revokeSubscription,
  saveDealer,
  selectSelectedDealer,
  setDealerActive,
  setTrial
} from '@/features/dealers/dealersSlice'
import { selectAssignablePlans } from '@/features/plans/plansSlice'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { Field, SelectInput, TextInput } from '@/components/ui/Field'
import { DataRow, EmptyState, Section } from '@/components/ui/Surface'
import { Badge, RoleBadge, StatusBadge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SubDealerFormModal } from './SubDealerFormModal'
import {
  dealerRole,
  formatCurrency,
  formatDate,
  formatRelative,
  initialsOf
} from '@/lib/format'
import { useAsyncAction } from '@/lib/useAsyncAction'
import type { SubDealer } from '@/types'

const TRIAL_PRESETS = [7, 14, 30]

export function DealerDrawer(): ReactNode {
  const dispatch = useAppDispatch()
  const dealer = useAppSelector(selectSelectedDealer)
  const subDealers = useAppSelector((s) => s.dealers.subDealers)
  const subsLoading = useAppSelector((s) => s.dealers.subDealersLoading)
  const plans = useAppSelector(selectAssignablePlans)
  const { isPending, run } = useAsyncAction()

  const [name, setName] = useState('')
  const [clients, setClients] = useState('')
  const [trialDays, setTrialDays] = useState('14')
  const [planId, setPlanId] = useState('')
  const [subDealerForm, setSubDealerForm] = useState<{ open: boolean; editing: SubDealer | null }>({
    open: false,
    editing: null
  })
  const [confirm, setConfirm] = useState<
    | { kind: 'deleteDealer' }
    | { kind: 'revoke' }
    | { kind: 'deleteSub'; sub: SubDealer }
    | null
  >(null)

  // Reset the editable fields whenever a different dealer is opened, and pick
  // up server-side changes after a save.
  useEffect(() => {
    if (!dealer) return
    setName(dealer.name)
    setClients(String(dealer.allowed_clients))
  }, [dealer])

  useEffect(() => {
    if (planId === '' && plans.length > 0) setPlanId(String(plans[0].id))
  }, [plans, planId])

  if (!dealer) return null

  const role = dealerRole(dealer)
  const isPrimary = role === 'primary'
  const close = (): void => {
    dispatch(dealerSelected(null))
  }

  const accountDirty =
    name.trim() !== dealer.name || Number(clients) !== dealer.allowed_clients
  const accountValid = name.trim().length >= 2 && Number(clients) >= 1

  const selectedPlan = plans.find((p) => String(p.id) === planId)
  const days = Number(trialDays)
  const trialValid = Number.isFinite(days) && days >= 1 && days <= 365

  return (
    <>
      <Drawer
        open
        onClose={close}
        title={
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded border border-accent/40 bg-accent/10 text-[12px] font-bold text-accent">
              {initialsOf(dealer.name)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold text-fg">{dealer.name}</p>
              <p className="truncate text-[11.5px] text-muted">{dealer.email}</p>
            </div>
          </div>
        }
        subtitle={<RoleBadge role={role} />}
      >
        {/* ---- Overview ---- */}
        <Section title="Overview">
          <dl className="divide-y divide-line">
            <DataRow label="Dealer ID">
              <span className="font-mono">{dealer.user_id}</span>
            </DataRow>
            <DataRow label="Subscription">
              <StatusBadge status={dealer.status} />
            </DataRow>
            {dealer.subscription_end && (
              <DataRow label="Ends">
                {formatDate(dealer.subscription_end)}
                {dealer.days_remaining > 0 && (
                  <span className="ml-1.5 text-muted">
                    ({dealer.days_remaining} day{dealer.days_remaining === 1 ? '' : 's'} left)
                  </span>
                )}
              </DataRow>
            )}
            <DataRow label="Email verified">
              {dealer.is_verified ? (
                <Badge tone="green">Verified</Badge>
              ) : (
                <Badge tone="amber">Unverified</Badge>
              )}
            </DataRow>
            <DataRow label="Account">
              {dealer.is_active ? (
                <Badge tone="green">Active</Badge>
              ) : (
                <Badge tone="red">Disabled</Badge>
              )}
            </DataRow>
            <DataRow label="Last sign-in">{formatRelative(dealer.last_login)}</DataRow>
            <DataRow label="Registered">{formatDate(dealer.created_at)}</DataRow>
          </dl>
        </Section>

        {/* ---- Subscription: only a primary account carries one ---- */}
        {isPrimary && (
          <Section title="Subscription" icon={CreditCard}>
            <div className="space-y-4">
              <div>
                <p className="label-caps mb-1.5 text-muted">Trial</p>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-20">
                    <TextInput
                      type="number"
                      min={1}
                      max={365}
                      value={trialDays}
                      onChange={(e) => setTrialDays(e.target.value)}
                      aria-label="Trial length in days"
                    />
                  </div>
                  {/* Presets sit beside the field they fill, before the action. */}
                  <div className="flex gap-1">
                    {TRIAL_PRESETS.map((preset) => (
                      <Button
                        key={preset}
                        variant="ghost"
                        size="sm"
                        className={
                          Number(trialDays) === preset
                            ? 'border-accent/60 text-accent'
                            : undefined
                        }
                        onClick={() => setTrialDays(String(preset))}
                      >
                        {preset}d
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="primary"
                    icon={CalendarClock}
                    disabled={!trialValid}
                    loading={isPending('trial')}
                    onClick={() =>
                      void run('trial', () =>
                        dispatch(setTrial({ userId: dealer.user_id, days })).unwrap()
                      )
                    }
                  >
                    {dealer.status === 'Trial' ? 'Extend trial' : 'Start trial'}
                  </Button>
                </div>
              </div>

              <div>
                <p className="label-caps mb-1.5 text-muted">Paid plan</p>
                {plans.length === 0 ? (
                  <p className="rounded-md border border-dashed border-line px-3 py-2.5 text-[11.5px] text-muted">
                    No active plans yet. Create one on the Plans page to assign it here.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[200px] flex-1">
                      <SelectInput
                        value={planId}
                        onChange={(e) => setPlanId(e.target.value)}
                        aria-label="Plan to assign"
                      >
                        {plans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.plan_name} · {formatCurrency(p.price)} · {p.duration_days}d
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                    <Button
                      variant="primary"
                      icon={CheckCircle2}
                      loading={isPending('assign')}
                      disabled={!selectedPlan}
                      onClick={() => {
                        if (!selectedPlan) return
                        void run('assign', () =>
                          dispatch(
                            assignPlan({
                              userId: dealer.user_id,
                              planId: selectedPlan.id,
                              planName: selectedPlan.plan_name
                            })
                          ).unwrap()
                        )
                      }}
                    >
                      Assign plan
                    </Button>
                  </div>
                )}
                <p className="mt-1.5 text-[11px] text-subtle">
                  Assigning a plan supersedes any running trial for the plan&rsquo;s duration.
                </p>
              </div>

              <div className="border-t border-line pt-3">
                <Button
                  variant="subtle"
                  size="sm"
                  icon={ShieldOff}
                  onClick={() => setConfirm({ kind: 'revoke' })}
                >
                  Revoke subscription
                </Button>
              </div>
            </div>
          </Section>
        )}

        {/* ---- Account ---- */}
        <Section title="Account">
          <div className="space-y-3">
            <Field label="Display name">
              {(id) => (
                <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>
            <Field
              label="Allowed clients"
              hint="How many trading clients this account may connect."
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="number"
                  min={1}
                  value={clients}
                  onChange={(e) => setClients(e.target.value)}
                />
              )}
            </Field>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                variant="primary"
                icon={Save}
                disabled={!accountDirty || !accountValid}
                loading={isPending('save')}
                onClick={() =>
                  void run('save', () =>
                    dispatch(
                      saveDealer({
                        userId: dealer.user_id,
                        body: { name: name.trim(), allowed_clients: Number(clients) }
                      })
                    ).unwrap()
                  )
                }
              >
                Save changes
              </Button>
              <Button
                variant="secondary"
                icon={dealer.is_active ? Ban : CheckCircle2}
                loading={isPending('active')}
                onClick={() =>
                  void run('active', () =>
                    dispatch(
                      setDealerActive({ userId: dealer.user_id, active: !dealer.is_active })
                    ).unwrap()
                  )
                }
              >
                {dealer.is_active ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="subtle"
                icon={Trash2}
                className="ml-auto"
                onClick={() => setConfirm({ kind: 'deleteDealer' })}
              >
                Delete
              </Button>
            </div>
          </div>
        </Section>

        {/* ---- Sub-dealers ---- */}
        {isPrimary && (
          <Section
            title={'Sub-dealers' + (subDealers.length ? ' (' + subDealers.length + ')' : '')}
            icon={Users}
            actions={
              <Button
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={() => setSubDealerForm({ open: true, editing: null })}
              >
                Add
              </Button>
            }
          >
            {subsLoading ? (
              <div className="label-caps flex items-center gap-2 px-1 py-6 text-muted">
                <Loader2 size={14} className="animate-spin text-accent" aria-hidden />
                Loading sub-dealers
              </div>
            ) : subDealers.length === 0 ? (
              <EmptyState
                icon={UserPlus}
                title="No sub-dealers"
                description="Sub-dealers share this account's subscription and get their own sign-in."
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Plus}
                    onClick={() => setSubDealerForm({ open: true, editing: null })}
                  >
                    Add sub-dealer
                  </Button>
                }
              />
            ) : (
              <ul className="divide-y divide-line">
                {subDealers.map((sub) => (
                  <li key={sub.user_id} className="flex items-center gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-[12.5px] font-medium text-fg">{sub.name}</p>
                        {!sub.is_active && <Badge tone="red">Disabled</Badge>}
                      </div>
                      <p className="truncate text-[11px] text-muted">
                        <span className="font-mono">{sub.user_id}</span> · {sub.email} ·{' '}
                        {sub.allowed_clients} seat{sub.allowed_clients === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Pencil}
                        aria-label={'Edit ' + sub.name}
                        onClick={() => setSubDealerForm({ open: true, editing: sub })}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        aria-label={'Delete ' + sub.name}
                        className="text-negative hover:bg-negative/10 hover:text-negative"
                        onClick={() => setConfirm({ kind: 'deleteSub', sub })}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}
      </Drawer>

      <SubDealerFormModal
        open={subDealerForm.open}
        editing={subDealerForm.editing}
        parentUserId={dealer.parent_user_id}
        onClose={() => setSubDealerForm({ open: false, editing: null })}
      />

      <ConfirmDialog
        open={confirm?.kind === 'deleteDealer'}
        title="Delete this dealer?"
        confirmLabel="Delete dealer"
        loading={isPending('delete')}
        message={
          <>
            <p>
              <strong className="font-semibold text-fg">{dealer.name}</strong>{' '}
              ({dealer.email}) will be removed from the cloud.
            </p>
            {isPrimary && (
              <p className="mt-2">
                This is a primary account, so its sub-dealers go with it. Trading data on their own
                machines is not affected.
              </p>
            )}
          </>
        }
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          void run('delete', () => dispatch(deleteDealer(dealer.user_id)).unwrap()).then(() =>
            setConfirm(null)
          )
        }
      />

      <ConfirmDialog
        open={confirm?.kind === 'revoke'}
        title="Revoke this subscription?"
        confirmLabel="Revoke"
        loading={isPending('revoke')}
        message={
          <p>
            {dealer.name} loses plan access immediately. You can start a new trial or assign a plan
            again at any time.
          </p>
        }
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          void run('revoke', () => dispatch(revokeSubscription(dealer.user_id)).unwrap()).then(() =>
            setConfirm(null)
          )
        }
      />

      <ConfirmDialog
        open={confirm?.kind === 'deleteSub'}
        title="Delete this sub-dealer?"
        confirmLabel="Delete sub-dealer"
        loading={isPending('deleteSub')}
        message={
          confirm?.kind === 'deleteSub' ? (
            <p>
              <strong className="font-semibold text-fg">{confirm.sub.name}</strong>{' '}
              ({confirm.sub.email}) will lose access straight away.
            </p>
          ) : null
        }
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind !== 'deleteSub') return
          void run('deleteSub', () =>
            dispatch(deleteSubDealer(confirm.sub.user_id)).unwrap()
          ).then(() => setConfirm(null))
        }}
      />
    </>
  )
}
